import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// OPTIONAL: agar tumhare paas ServQual/SUSAN logo base64 ya import hai
import susanLogo from "../../Assets/ServQual-Logo-White.png";

const generatePhishingReport = (userData, assessmentData, logoImg) => {
  // A4 portrait in points
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageWidth = doc.internal.pageSize.getWidth();   // 595
  const pageHeight = doc.internal.pageSize.getHeight(); // 842

  // Brand colors (same palette jaisa SecurityModel me hai)
  const brandColors = {
    primary: "#061c31",
    secondary: "#061c31",
    accent: "#FFFF00",
    textDark: "#2C3E50",
    textLight: "#5A6C7D",
    background: "#F8F9FA", 
    white: "#FFFFFF",
    lightGray: "#E9ECEF"
  };

  // --------- COMMON HEADER ----------
  const drawHeader = (docInstance, pageNo, totalPages) => {
    // Blue bar
    docInstance.setFillColor(brandColors.secondary);
    docInstance.rect(0, 0, pageWidth, 80, "F");

    // Logo (left)
    try {
      if (logoImg) {
        docInstance.addImage(logoImg, "PNG", 30, 15, 50, 50);
      } else {
        // simple fallback icon
        docInstance.setFillColor(brandColors.accent);
        docInstance.circle(55, 40, 20, "F");
        docInstance.setTextColor(brandColors.secondary);
        docInstance.setFontSize(18);
        docInstance.setFont("helvetica", "bold");
        docInstance.text("S", 50, 46);
      }
    } catch {
      // ignore image error
    }

    // Main title
    docInstance.setTextColor(brandColors.white);
    docInstance.setFont("helvetica", "bold");
    docInstance.setFontSize(22);
    docInstance.text("SUSAN Phishing Assessment Report", 100, 38);

    // Subtitle – date
    docInstance.setFont("helvetica", "normal");
    docInstance.setFontSize(10);
    docInstance.text(
      `Generated on ${new Date().toLocaleDateString()}`,
      100,
      55
    );

    // Page number right side
    docInstance.setFontSize(10);
    docInstance.setFont("helvetica", "normal");
    const pageLabel =
      totalPages > 0 ? `Page ${pageNo} of ${totalPages}` : `Page ${pageNo}`;
    const labelWidth = docInstance.getTextWidth(pageLabel);
    docInstance.text(pageLabel, pageWidth - 30 - labelWidth, 30);
  };

  // --------- COMMON FOOTER ----------
  const drawFooter = (docInstance, pageNo, totalPages) => {
    const footerHeight = 35;

    // Blue footer bar
    docInstance.setFillColor(brandColors.secondary);
    docInstance.rect(
      0,
      pageHeight - footerHeight,
      pageWidth,
      footerHeight,
      "F"
    );

    // Left text
    docInstance.setTextColor(brandColors.white);
    docInstance.setFontSize(9);
    docInstance.setFont("helvetica", "bold");
    docInstance.text(
      "SUSAN - Secure Your Digital Assets",
      30,
      pageHeight - 15
    );

    // Right page x of y
    const pageLabel = `Page ${pageNo} of ${totalPages}`;
    const labelWidth = docInstance.getTextWidth(pageLabel);
    docInstance.setFont("helvetica", "normal");
    docInstance.text(
      pageLabel,
      pageWidth - 30 - labelWidth,
      pageHeight - 15
    );

    // Generated timestamp (left bottom)
    docInstance.setFontSize(7);
    docInstance.text(
      `Generated: ${new Date().toLocaleString()}`,
      30,
      pageHeight - 4
    );
  };

  // --------- FIRST PAGE CONTENT ----------
  drawHeader(doc, 1, 0);

  // Top margin after header - INCREASED GAP
  let yPos = 110; // Changed from 100

  // EXECUTIVE SUMMARY
  doc.setFillColor(brandColors.background);
  doc.setDrawColor(brandColors.primary);
  doc.setLineWidth(2);
  doc.roundedRect(30, yPos, pageWidth - 60, 120, 8, 8, "FD"); // Removed +10 offset

  // Score circle
  const scoreX = pageWidth - 80;
  const scoreY = yPos + 60;
  doc.setFillColor(brandColors.primary);
  doc.circle(scoreX, scoreY, 40, "F");
  doc.setFillColor(brandColors.white);
  doc.circle(scoreX, scoreY, 32, "F");

  const scaledScore = assessmentData.percentage ?? 0;
  const scoreStr = `${scaledScore}%`;
  doc.setTextColor(brandColors.secondary);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  const scoreWidth = doc.getTextWidth(scoreStr);
  doc.text(scoreStr, scoreX - scoreWidth / 2, scoreY + 6);

  // Summary text
  doc.setTextColor(brandColors.textDark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Executive Summary", 45, yPos + 25); // Adjusted from +35

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const answered = assessmentData.answeredQuestions ?? assessmentData.totalQuestions ?? 0;
  doc.text(
    `Total Questions: ${assessmentData.totalQuestions}`,
    45,
    yPos + 50 // Adjusted spacing
  );
  doc.text(
    `Correct Answers: ${assessmentData.correctAnswers}`,
    45,
    yPos + 68
  );
  doc.text(`Final Score: ${assessmentData.percentage}%`, 45, yPos + 86);
  doc.text(
    `Answered: ${answered}`,
    45,
    yPos + 104
  );

  const userName = userData?.name || userData?.["cognito:username"] || "N/A";
  doc.text(`User: ${userName}`, 250, yPos + 50);
  doc.text(`Email: ${userData?.email || "N/A"}`, 250, yPos + 68);

  yPos += 150; // INCREASED GAP after summary

  // DETAILED TABLE TITLE
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(brandColors.secondary);
  doc.text("Detailed Question Analysis", 30, yPos);
  yPos += 20; // INCREASED GAP before table (was 15)

  // TABLE
  const tableColumn = ["Sr. No.", "Scenario", "Your Answer", "Correct", "Result"];
  const tableRows = [];
  assessmentData.questions.forEach((q, index) => {
    const userAnswer = assessmentData.selectedAnswers[index] || "N/A";
    const isCorrect = userAnswer === q.correctAnswer;
    const status =
      userAnswer === "N/A" ? "Not Answered" : isCorrect ? "Correct" : "Wrong";

    tableRows.push([
      (index + 1).toString(),
      q.scenario,
      userAnswer,
      q.correctAnswer,
      status
    ]);
  });

  const severityColors = {
    Correct: [76, 175, 80],
    Wrong: [244, 67, 54],
    "Not Answered": [158, 158, 158]
  };

  autoTable(doc, {
    startY: yPos,
    head: [tableColumn],
    body: tableRows,
    margin: { left: 30, right: 30, top: 0, bottom: 60 },
    tableWidth: pageWidth - 60,
    theme: "striped",
    styles: {
      fontSize: 9,
      cellPadding: 4, // Increased from 3
      textColor: brandColors.textDark,
      overflow: "linebreak",
      font: "helvetica"
    },
    headStyles: {
      fillColor: brandColors.secondary,
      textColor: brandColors.white,
      fontStyle: "bold",
      fontSize: 10,
      halign: "center",
      cellPadding: 5 // More padding in header
    },
    columnStyles: {
      0: { cellWidth: 30, halign: "center" },
      1: { cellWidth: 220 },
      2: { cellWidth: 70, halign: "center" },
      3: { cellWidth: 70, halign: "center" },
      4: { cellWidth: 80, halign: "center" }
    },
    didParseCell: (data) => {
      if (data.column.index === 4 && data.section === "body") {
        const val = data.cell.raw;
        if (severityColors[val]) {
          data.cell.styles.textColor = severityColors[val];
          data.cell.styles.fontStyle = "bold";
        }
      }
    }
  });

  // RECOMMENDATIONS - INCREASED GAP
  let afterTableY = doc.lastAutoTable.finalY + 25; // Changed from 20
  if (afterTableY > pageHeight - 100) {
    doc.addPage();
    drawHeader(doc, doc.internal.getNumberOfPages(), 0);
    afterTableY = 110;
  }

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(brandColors.secondary);
  doc.text("Recommendations", 30, afterTableY);
  afterTableY += 18; // INCREASED GAP (was 14)

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(brandColors.textDark);

  if (assessmentData.percentage < 60) {
    doc.text(
      "• We strongly recommend revisiting the phishing awareness training material.",
      30,
      afterTableY
    );
    afterTableY += 14; // Increased line spacing (was 12)
    doc.text(
      "• Focus on identifying suspicious emails, links and verifying sender identity.",
      30,
      afterTableY
    );
    afterTableY += 14;
    doc.text(
      "• Always report suspected phishing attempts to your Security / IT team.",
      30,
      afterTableY
    );
  } else if (assessmentData.percentage < 80) {
    doc.text(
      "• Good performance! Review the questions you missed for further improvement.",
      30,
      afterTableY
    );
    afterTableY += 14;
    doc.text(
      "• Continue to stay updated on current phishing tactics and red flags.",
      30,
      afterTableY
    );
  } else {
    doc.text(
      "• Excellent work! You demonstrate strong phishing awareness.",
      30,
      afterTableY
    );
    afterTableY += 14;
    doc.text(
      "• Keep practicing safe email and link-handling habits in daily work.",
      30,
      afterTableY
    );
  }

  // --------- FINAL HEADER/FOOTER PASS ----------
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    drawHeader(doc, i, pageCount);
    drawFooter(doc, i, pageCount);
  }

  doc.save(
    `SUSAN_Phishing_Assessment_Report_${new Date()
      .toISOString()
      .split("T")[0]}.pdf`
  );
};

export default generatePhishingReport;
